import {
  CheckCircledIcon,
  ExclamationTriangleIcon,
  InfoCircledIcon,
} from "@radix-ui/react-icons";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { collaboratorNotificationsQueryOptions } from "@/lib/api";
import { useStoredString } from "@/lib/hooks";

/** Per-browser, see `useStoredString`. */
const SEEN_KEY = "collaborator-notifications-seen-at";

/**
 * Tells you when your access to somebody else's feed changed, and when a post you put
 * forward was accepted or passed over.
 *
 * 🔑 THESE EVENTS HAPPEN IN SOMEBODY ELSE'S BROWSER. Being added to a feed, having your
 * capabilities changed, being removed, and having your suggestion resolved were all silent —
 * you found out by noticing a feed had appeared in your list, or by pressing a button that
 * had stopped working, or never. Nothing else in this app has that problem, because every
 * other change is made by the person looking at it.
 *
 * Suggesting is the capability given to the people trusted least, so they are the least
 * likely to have anywhere else to notice the answer — which is exactly why the answer comes
 * here.
 *
 * Derived on the server from the collaborator audit log rather than stored, so there is no
 * table and nothing to keep in step. "Seen" is the one piece of state, and it lives in this
 * browser: the cost of forgetting it is a badge that reappears, which is cheaper than a
 * column and a migration for a dot.
 */
export function CollaboratorNotifications() {
  const [open, setOpen] = useState(false);
  const [seenAt, setSeenAt] = useStoredString(SEEN_KEY);
  const { data: notifications } = useQuery(
    collaboratorNotificationsQueryOptions(),
  );

  const all = notifications ?? [];
  const unseen = seenAt ? all.filter((n) => !n.at || n.at > seenAt) : all;

  // Nothing NEW means nothing to show. The server keeps 30 days of these so the popover
  // has history worth reading, but a badge that sits there for a month after you have read
  // it is furniture, and furniture is what people stop seeing.
  //
  // `!open` matters: marking them seen is what empties `unseen`, so without it this would
  // unmount the popover the instant you opened it and you could never read the thing the
  // badge was pointing at.
  if (!open && unseen.length === 0) {
    return null;
  }

  // Marked seen on CLOSE, not on open: closing is the point at which you have had the
  // chance to read them.
  const markSeen = () => {
    const newest = all.reduce<string | null>(
      (latest, n) => (n.at && (!latest || n.at > latest) ? n.at : latest),
      null,
    );
    if (newest) {
      setSeenAt(newest);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          markSeen();
        }
      }}
    >
      <PopoverTrigger
        tabIndex={0}
        onMouseEnter={() => setOpen(true)}
        aria-label="Changes to feeds shared with you"
      >
        <div className="px-2 py-1 text-xs bg-red-11 dark:bg-reddark-9 text-slate-1 dark:text-slatedark-1 rounded-md">
          {unseen.length}
        </div>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        alignOffset={-32}
        className="flex max-w-80 flex-col gap-3 p-3"
        onMouseLeave={() => {
          setOpen(false);
          markSeen();
        }}
      >
        {all.map((n) => {
          const body = (
            <div className="flex items-start gap-2 text-sm">
              <span className="mt-0.5 shrink-0">
                {n.type === "success" && <CheckCircledIcon />}
                {n.type === "info" && <InfoCircledIcon />}
                {n.type === "warning" && <ExclamationTriangleIcon />}
              </span>
              <span>{n.text}</span>
            </div>
          );

          // A removal has no link on purpose: that feed is closed to them now, and a
          // notification that 403s when pressed is worse than one that only tells you.
          // Everything else links to the feed, suggestions included — the whole point of
          // being told your post went in is being able to go and look at it.
          return n.link ? (
            <Link
              key={`${n.category}-${n.feed_id}-${n.at ?? ""}`}
              to={n.link}
              className="no-underline hover:underline"
              onClick={() => {
                setOpen(false);
                markSeen();
              }}
            >
              {body}
            </Link>
          ) : (
            <div key={`${n.category}-${n.feed_id}-${n.at ?? ""}`}>{body}</div>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
